import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';
import { API_BASE_URL } from 'Shared/fetch';
import useFetch, { CachePolicies } from 'use-http';

import { updateMediaInQueryClient } from '@/queries/media';
import { useSelector } from '@/redux/helper';
import { updateAlbumData } from '@/redux/slices/myAlbum/actions';

import { updateHabitatAlbumData } from '../../routes/habitat/components/Album/actions';

interface UseToggleMediaLikeArgs {
  mediaId?: string;
  type?: 'video' | 'photo';
  isLiked?: boolean;
  likes?: number;
}

/**
 * Shared like/unlike logic for media (videos & photos). Calls the like endpoint and keeps the
 * redux album slices and the react-query cache in sync so the state is consistent as users
 * navigate back and forth. Liking a video adds it to the user's My Journal.
 */
const useToggleMediaLike = ({ mediaId, type, isLiked: initialIsLiked, likes: initialLikes }: UseToggleMediaLikeArgs) => {
  const queryClient = useQueryClient();
  const logged = useSelector((state) => state.user.logged);
  const dispatch = useDispatch();
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const [likes, setLikes] = useState(initialLikes ?? 0);
  const togglePendingRef = useRef(false);
  const { put } = useFetch(API_BASE_URL, { credentials: 'include', cachePolicy: CachePolicies.NO_CACHE });

  useEffect(() => {
    setIsLiked(initialIsLiked);
  }, [initialIsLiked, mediaId]);

  useEffect(() => {
    setLikes(initialLikes ?? 0);
  }, [initialLikes, mediaId]);

  // Returns the new isLiked value, or undefined if nothing happened (e.g. no mediaId).
  const toggle = async (): Promise<boolean | undefined> => {
    if (!mediaId || togglePendingRef.current) {
      return undefined;
    }

    togglePendingRef.current = true;
    try {
      await put(`${type === 'photo' ? 'photos' : 'videos'}/${mediaId}/like`);

      const newIsLiked = !isLiked;
      const currentLikes = likes ?? 0;
      const newLikes = newIsLiked ? currentLikes + 1 : Math.max(0, currentLikes - 1);

      setIsLiked(newIsLiked);
      setLikes(newLikes);

      const data = { isLiked: newIsLiked, usersLike: newLikes };
      const payload = { id: mediaId, data };
      dispatch(updateHabitatAlbumData(payload));
      dispatch(updateAlbumData(payload));
      updateMediaInQueryClient(queryClient, mediaId, data, type);

      return newIsLiked;
    } catch (err) {
      console.error('Error trying to like content:', err);
      return undefined;
    } finally {
      togglePendingRef.current = false;
    }
  };

  return { logged, isLiked, likes, toggle };
};

export default useToggleMediaLike;
